import clsx from 'clsx'
import React, { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'

import { SystemAlert } from '@cms/components/data/Alert'
import { Button, CopyToClipboardButton } from '@cms/components/data/Button'
import { ControlledSystemDialog } from '@cms/components/data/Dialog'
import { ControlledDropdown, Dropdown } from '@cms/components/data/Dropdown'
import { ControlledAssetInput } from '@cms/components/data/FileInput'
import { ControlledSystemMultiTab } from '@cms/components/data/MultiTab'
import { ControlledRadio } from '@cms/components/data/Radio'
import { CancelButton } from '@cms/components/data/shared'
import { ControlledSystemTextInput } from '@cms/components/data/TextInput'
import { GuestForm } from '@cms/components/forms/UserForm'
import { ViewLogIcon } from '@cms/components/icons'
import { Chip } from '@cms/components/ui/Chip'
import { LabeledSection } from '@cms/components/ui/LabeledSection'
import { SystemTooltip } from '@cms/components/ui/Tooltip'
import api from '@cms/utils/api'
import { BROWSER_EVENTS, POST_SET_VALUE_OPTIONS } from '@cms/utils/constants'
import { StoryFormData } from '@cms/utils/forms/story'
import { generateSearchParam, generateStoryLink, mapLookupsToOptions, renderDateTime, renderVersionInfo } from '@cms/utils/helpers'
import { authorsLookupsQuery, subtypesLookupsQuery, verticalsLookupsQuery } from '@cms/utils/queries'
import { useAppDispatch, useAppSelector } from '@cms/utils/store/hooks'
import { getAllStoryVersionsAsyncAction, toggleIsGeneralSettingsOpenAction } from '@cms/utils/store/slices/editor.slice'
import { groupBy, IDropdownOptionV2, ISystemGuest, ISystemResponse, ISystemStoryVersion, ISystemTag, shortenText, STOP_WORDS, SUBTYPES, TAssetMetadata, TEAMS, USERS, VALUES, WORKFLOWS } from '@dl/declarations'
import { CustomEmbedPluginActions, CustomEmbedPluginContainer, CustomEmbedPluginNote } from '../nodes/CustomEmbedPluginContainer'
import { AssetMetadataDialog } from './AssetMenu'
import { RegenerateURLMenu } from './RegenerateURLMenu'

import { BiPlus, BiSolidUserDetail } from 'react-icons/bi'
import { HiOutlineUserPlus } from 'react-icons/hi2'
import { MdArrowDropDown, MdArrowDropUp } from 'react-icons/md'

export const GeneralSettingsButton = () => {
    const dispatch = useAppDispatch()
    const { isGeneralSettingsOpen } = useAppSelector((state) => state.editor)

    return (
        <button
            type='button'
            className='cursor-pointer flex items-center xl:items-end text-base xl:text-lg'
            onClick={() => dispatch(toggleIsGeneralSettingsOpenAction(!isGeneralSettingsOpen))}
        >
            <span>General Settings</span>
            {isGeneralSettingsOpen ? <MdArrowDropUp className='size-5 xl:size-7' size={28} /> : <MdArrowDropDown className='size-5 xl:size-7' size={28} />}
        </button>
    )
}

export const EditorTopBarHeader = () => {
    const dispatch = useAppDispatch()
    const { isGeneralSettingsOpen, story } = useAppSelector((state) => state.editor)

    return (
        <button
            type='button'
            className='cursor-pointer flex items-center'
            onClick={() => dispatch(toggleIsGeneralSettingsOpenAction(!isGeneralSettingsOpen))}
        >
            <span
                className='font-bold text-base'
                title={story?.headline || 'General Settings'}
            >
                {shortenText(story?.headline || 'General Settings', 30)}
            </span>

            {isGeneralSettingsOpen ? <MdArrowDropUp className='size-5 xl:size-7' size={28} /> : <MdArrowDropDown className='size-5 xl:size-7' size={28} />}

            {story?.scheduleObject ? (
                <span
                    className='text-sm text-[#9E9E9E]'
                >
                    Scheduled {renderDateTime(story.scheduleObject.action_at)}
                </span>
            ) : story?.story_workflow === 'published' ? (
                <span
                    className='text-sm text-[#9E9E9E]'
                >
                    Published {renderDateTime(story.story_publish_date)}
                </span>
            ) : null}
        </button>
    )
}

const AddStoryGuestModal = () => {
    const { setValue, watch } = useFormContext<StoryFormData>()

    const selected_guests = watch('guests');

    const handleCreateRecordOverride = async (data: any) => {
        handleCancelOverride();
        setValue('guests', [...selected_guests, { ...data, _id: (selected_guests.length + 1) }], POST_SET_VALUE_OPTIONS)
    }

    const handleCancelOverride = () => {
        window.dispatchEvent(new Event(BROWSER_EVENTS.CLOSE_MODAL));
    }

    return (
        <ControlledSystemDialog
            trigger={{
                component: (
                    <Button
                        inputVariant='white_text_dark_bg'
                        rightIcon={<BiSolidUserDetail size={18} />}
                    >
                        Add guest
                    </Button>
                )
            }}
            content={{
                component: (
                    <GuestForm
                        headerOptions={{
                            show: false
                        }}
                        handleCreateRecordOverride={handleCreateRecordOverride}
                        handleCancelOverride={handleCancelOverride}
                    />
                )
            }}
            dialogProps={{
                title: 'Add guest',
                maxWidth: 'md',
                disableScrollLock: false
            }}
            actionsProps={{
                hide: true,
            }}
        />
    )
}

const MIN_TEXT_CONTENT_ELEMENTS_FOR_SUGGESTIONS = 3;

const EmbedTopicModal = () => {
    const { watch, setValue } = useFormContext<StoryFormData>()

    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [allTagsSuggestions, setAllTagsSuggestions] = useState<{
        isFetched: boolean;
        suggestions: Array<ISystemTag>
    }>({
        isFetched: false,
        suggestions: []
    })

    const [selected_tags, inserted_content_elements] = watch(['tags', 'content_elements']);

    const text_content_elements = useMemo(() => {
        return inserted_content_elements.filter(item => item.type === 'text');
    }, [inserted_content_elements]);

    const usedTagsSuggestions = useMemo(() => {
        if (allTagsSuggestions.isFetched && allTagsSuggestions.suggestions.length > 0) {
            return allTagsSuggestions.suggestions.filter((tag) => !selected_tags.includes(tag._id)).slice(0, 3)
        }

        return [];
    }, [allTagsSuggestions, selected_tags])

    /** 
    * Fetch tag suggestions based on the current text content
   */
    useEffect(() => {
        const timerId = setTimeout(() => {
            if (
                isDialogOpen &&
                text_content_elements.length > MIN_TEXT_CONTENT_ELEMENTS_FOR_SUGGESTIONS &&
                // selected_tags.length < 2 &&
                !allTagsSuggestions.isFetched
            ) {
                const wordsScoreByAppearance: Record<string, number> = {}

                text_content_elements.forEach((textContentElement) => {
                    const textElementWords = textContentElement.content
                        // remove html tags with inner content
                        .replace(/<[^>]*>/g, '')
                        // remove special characters
                        .replace(/[^\w\s]/g, '')
                        // split by spaces
                        .split(/\s+/)
                        // remove stop words and words with less than 3 characters
                        .filter((word) => word.length > 2 && !STOP_WORDS.has(word.toLowerCase()))

                    textElementWords.forEach((word) => {
                        wordsScoreByAppearance[word] = (wordsScoreByAppearance[word] || 0) + 1
                    })
                })

                const words = Object.keys(wordsScoreByAppearance)
                    .sort((a, b) => wordsScoreByAppearance[b] - wordsScoreByAppearance[a])
                    .slice(0, 10)

                if (!words.length) {
                    return
                }

                api.get<ISystemResponse<Array<ISystemTag>>>('/tags/suggestions', {
                    params: {
                        q: words.join(',')
                    }
                })
                    .then((response) => {
                        if (response.data) {
                            setAllTagsSuggestions({ isFetched: true, suggestions: response.data.data })
                        }
                    })
                    .catch(() => {
                        setAllTagsSuggestions({ isFetched: true, suggestions: [] });
                    });
            }
        }, 1000);

        return () => {
            clearTimeout(timerId);
        }
    }, [text_content_elements, selected_tags, allTagsSuggestions, isDialogOpen])


    const handleAddTagSuggestion = (tagSuggestion: ISystemTag) => {
        if (allTagsSuggestions.suggestions.length > 0) {
            setValue('tags', [...selected_tags, tagSuggestion._id], POST_SET_VALUE_OPTIONS);
        }
    }

    return (
        <ControlledSystemDialog
            trigger={{
                component: (
                    <Button
                        inputVariant='white_text_dark_bg'
                        rightIcon={<BiPlus size={18} />}
                    >
                        Embed topic
                    </Button>
                ),
                onIsDialogOpenChange: setIsDialogOpen
            }}
            content={{
                component: (
                    <CustomEmbedPluginContainer
                        embedTitle=''
                        embedDescription={(
                            <>
                                <b>Embed topics at the end of the article</b> to invite users to continue reading on the same topics within DL News.
                            </>
                        )}
                        instructionsScreenshotUrl='/images/embeds/topics.png'
                        embedActionComponent={(
                            <form className="flex flex-col gap-4">
                                <ControlledDropdown
                                    label='Add Related Topics'
                                    as='with-options-loader'
                                    optionsQueryKey='tagsLookupsQuery'
                                    valueKey='_id'
                                    name='tags'
                                    inputSize='full'
                                    allowMultiSelect
                                    maxChipsCount={1}
                                    customRenderLabel={(option) => (
                                        <span className='flex items-center gap-4'>
                                            <span>{option.label}</span>
                                            <span>{(option as any).tag_story_aggregate}</span>
                                        </span>
                                    )}
                                />

                                {text_content_elements.length <= MIN_TEXT_CONTENT_ELEMENTS_FOR_SUGGESTIONS && (
                                    <p className='text-sm text-cs-600'>
                                        Add more text content to get topic suggestions.
                                    </p>
                                )}

                                {
                                    usedTagsSuggestions.length > 0 ? (
                                        <div className='flex flex-col xl:gap-2 gap-1'>
                                            <p className='font-medium'>Suggestions:</p>
                                            <div className='flex flex-col items-start gap-1'>
                                                {usedTagsSuggestions.map((suggestion, index) => (
                                                    <p
                                                        key={suggestion._id}
                                                        className='flex items-center gap-2'
                                                    >
                                                        <span>{index + 1}</span>
                                                        <Chip
                                                            variant='none'
                                                            wrapperClassName='cursor-pointer'
                                                            onClick={() => handleAddTagSuggestion(suggestion)}
                                                        >
                                                            {suggestion.name}
                                                        </Chip>
                                                        <span>Used on: {suggestion.tag_story_aggregate || "0"} articles</span>
                                                    </p>
                                                ))}
                                            </div>
                                        </div>
                                    ) : <div />
                                }


                                <CustomEmbedPluginActions
                                    handleCancel={() => { }}
                                    handleSubmit={() => { }}
                                />
                            </form>
                        )}
                        instructionsNotesComponent={(
                            <div className="w-full">
                                <CustomEmbedPluginNote
                                    wrapperClassName='w-full flex items-center gap-8'
                                    notes={[
                                        {
                                            title: "Adding Topics",
                                            description: "Type to create your own or autocomplete a known topic. Drag and drop the topics to reorder them."
                                        },
                                        {
                                            title: "Saving and Checking Topics",
                                            description: "After choosing all your topics, you must click on SAVE. The CMS Page will be refreshed. You can check your saved topics right here in the text field."
                                        },
                                        {
                                            title: "Updating Topics",
                                            description: "You can access this feature (Related Topics) and change the topics you want (remove or add). To do this, simply repeat all the steps again."
                                        }
                                    ]}
                                />
                            </div>
                        )}
                    />
                )
            }}
            dialogProps={{
                title: 'Embed/Topics',
                disableScrollLock: false,
            }}
            actionsProps={{
                hide: true
            }}
        />
    )
}

const SelectAssetModal = () => {
    const { story } = useAppSelector((state) => state.editor)

    const { setValue, watch } = useFormContext<StoryFormData>()

    const main_asset_info = watch('main_asset_info');
    const usedDefaultValues = main_asset_info || story?.mainAssetObject || null;

    return (
        <LabeledSection
            label='Assets'
        >
            <ControlledAssetInput
                name="main_asset"
                initialValue={story?.mainAssetObject || null}
                showFullPreview={false}
                mainAction={{
                    show: true,
                    actionText: 'Main asset',
                    dialogTitle: 'Select main asset',
                }}
                editAction={{
                    show: Boolean(usedDefaultValues),
                    actionText: 'Edit Metadata',
                    contentComponent: (
                        <AssetMetadataDialog
                            key={usedDefaultValues?._id ?? 'none'}
                            defaultValues={{
                                _id: usedDefaultValues?._id || "",
                                headline: usedDefaultValues?.headline || "",
                                credit: usedDefaultValues?.credit || "",
                                copyright: usedDefaultValues?.copyright || "",
                                article_caption: usedDefaultValues?.article_caption || "",
                                src_url: usedDefaultValues?.src_url || "",
                            }}
                            handleSaveChanges={(data: TAssetMetadata) => {
                                setValue('main_asset_info', data, POST_SET_VALUE_OPTIONS);
                            }}
                        />
                    )
                }}
            />
        </LabeledSection>
    )
}

const ViewLogModal = () => {
    const dispatch = useAppDispatch()
    const [searchParams, setSearchParams] = useSearchParams()

    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [selectedVersionId, setSelectedVersionId] = useState<string | null>(searchParams.get(generateSearchParam("versions", "id")) || null)

    const { story, versions, currentVersion } = useAppSelector((state) => state.editor)

    const versionsToOptions = useMemo(() => {
        const mapped = (versions || []).map((version: ISystemStoryVersion) => {
            const label = renderVersionInfo(version, story?.published_story_version || '')

            return {
                _id: version._id,
                slug: version._id,
                label,
                groupKey: 'Versions',
            }
        })

        return mapped;
    }, [versions, story])

    const handleChangeVersion = (option: IDropdownOptionV2) => {
        if (option?._id !== selectedVersionId) {
            setSelectedVersionId(option?._id || null)
        }
    }

    const handleReviewSelectedVersion = () => {
        if (selectedVersionId) {
            setSearchParams((prev) => {
                prev.set(generateSearchParam("versions", "id"), selectedVersionId)
                return prev;
            }, { replace: true })
        }
    }

    useEffect(() => {
        if (currentVersion && currentVersion._id !== selectedVersionId) {
            setSelectedVersionId(currentVersion._id)
        }
    }, [currentVersion])

    useEffect(() => {
        if (isDialogOpen && story?.saved_story_version) {
            dispatch(getAllStoryVersionsAsyncAction({ id: story._id }))
        }
    }, [isDialogOpen, story])

    return (
        <ControlledSystemDialog
            trigger={{
                component: (
                    <Button
                        rightIcon={<ViewLogIcon />}
                    >
                        View log
                    </Button>
                ),
                onIsDialogOpenChange: setIsDialogOpen
            }}
            content={{
                component: (
                    <div className='flex flex-col gap-4'>
                        <SystemAlert severity="warning">
                            This log is still in progress for upcoming phases. You can review previous versions using the field below.
                        </SystemAlert>

                        <Dropdown
                            options={versionsToOptions}
                            handleChange={handleChangeVersion}
                            value={selectedVersionId || story?.saved_story_version || ''}
                            valueKey='slug'
                            searchOptions={{
                                allow: false,
                            }}
                            allowSingleDeselect={false}
                            inputSize='full'
                            menuSize='full'
                            disabled={versions.length === 0}
                        />
                    </div>
                )
            }}
            dialogProps={{
                title: 'View log',
                maxWidth: 'md',
                disableScrollLock: false,
                contentStyle: {
                    overflow: 'visible',
                }
            }}
            actionsProps={{
                submit: {
                    label: 'Review selected version',
                    onClick: handleReviewSelectedVersion
                }
            }}
        />
    )
}

interface GeneralSettingsComponentProps {
    presentationVersion?: 'compact' | 'expanded'
    rightColumnOptions?: {
        show: boolean
    }
}

export const GeneralSettingsComponent: React.FC<GeneralSettingsComponentProps> = (props) => {
    const {
        presentationVersion = 'expanded',
        rightColumnOptions = {
            show: true
        }
    } = props;

    const { profile: userProfile } = useAppSelector((state) => state.user)
    const { isGeneralSettingsOpen = true, story, mode } = useAppSelector((state) => state.editor)

    const { data: allVerticals } = verticalsLookupsQuery({}, { fetchOnce: true })
    const { data: allAuthors } = authorsLookupsQuery({}, { fetchOnce: true })
    const { data: allSubtypes } = subtypesLookupsQuery({
        used_for: 'stories',
        sort: 'order:asc',
    }, { fetchOnce: true })

    const { watch, setValue } = useFormContext<StoryFormData>()

    const [
        selected_subtype,
        selected_sub_vertical,
        selected_primary_vertical,
        selected_verticals,
        selected_authors,
        selected_guests,
        // selected_tags,
        // inserted_content_elements
    ] = watch([
        'subtype',
        'sub_vertical',
        'primary_vertical',
        'verticals',
        'authors',
        'guests',
        // 'tags', 
        // 'content_elements'
    ]);

    const verticalsLookupsToOptions = useMemo(() => {
        const filtered = (allVerticals || [])?.filter((item) => {
            if (selected_subtype) {
                return item.subtype === selected_subtype
            }

            return true
        })

        const mapped = mapLookupsToOptions(filtered, 'name', (item) => item.subtypeObject.slug)
        return groupBy(mapped, (item) => item.groupKey);
    }, [allVerticals, selected_subtype])

    const authorsLookupsToOptions = useMemo(() => {
        const filtered = (allAuthors || []).filter((author) => {
            if (author.slug === USERS.TECHNICAL_USER.SLUG) {
                return true;
            }

            if (selected_authors.includes(author._id)) {
                return true;
            }

            if (selected_subtype === SUBTYPES.RESEARCH.ID) {
                return author.team === TEAMS.RESEARCH.SLUG
            }

            if (selected_subtype === SUBTYPES.NEWS.ID) {
                return author.team === TEAMS.NEWSROOM.SLUG
            }

            return false;
        })

        const mapped = filtered.map((author) => ({
            _id: author._id,
            slug: author._id,
            label: author.name,
            groupKey: 'Authors',
        })) || []

        return mapped
    }, [allAuthors, selected_subtype, selected_authors])

    const verticalsForPrimaryVertical = useMemo(() => {
        const mapped = mapLookupsToOptions(allVerticals || [], 'name', (item) => item.subtypeObject.slug)

        return {
            Verticals: (mapped || [])
                .filter((v) => selected_verticals.includes(v._id)),
        }
    }, [allVerticals, selected_verticals])

    const handleAuthorAddMe = () => {
        if (userProfile?._id) {
            if (!selected_authors.includes(userProfile._id)) {
                setValue('authors', [...selected_authors, userProfile._id], POST_SET_VALUE_OPTIONS);
            }
        }
    }

    const handleRemoveGuest = (guest: ISystemGuest) => {
        setValue('guests', selected_guests.filter((item) => item._id !== guest._id), POST_SET_VALUE_OPTIONS);
    }

    /** 
     * Ensure primary vertical is always part of the selected verticals
    */
    useEffect(() => {
        if (selected_verticals.length > 0 && !selected_verticals.includes(selected_primary_vertical)) {
            setValue('primary_vertical', selected_verticals[0], POST_SET_VALUE_OPTIONS);
        }

        if ([SUBTYPES.RESEARCH.ID].includes(selected_subtype) && selected_verticals.length > 1) {
            setValue('verticals', [selected_verticals.at(-1)!], POST_SET_VALUE_OPTIONS);
        }
    }, [selected_subtype, selected_verticals])

    /*
     * Reset the form state when the selected subtype changes
    */
    useEffect(() => {
        if (story && selected_subtype !== story?.subtype) {
            setValue('primary_vertical', '', POST_SET_VALUE_OPTIONS);

            setValue('verticals', [], POST_SET_VALUE_OPTIONS);
        }
    }, [selected_subtype])

    const verticalsComponent = (
        <LabeledSection
            label='Sections'
            className='grow'
        >
            <SystemTooltip
                message={!selected_subtype ? 'Please select a content type first' : ''}
            >
                <ControlledRadio
                    name='verticals'
                    options={verticalsLookupsToOptions || {}}
                    valueKey='_id'
                    labelKey='label'
                    allowMultiSelect
                    distribution={{
                        type: 'grid'
                    }}
                    disabled={!selected_subtype}
                />
            </SystemTooltip>

            <p className='mt-2 flex items-start gap-2'>
                <span className='font-bold'>
                    Preview:
                </span>

                <span className='select-all'>
                    {generateStoryLink({
                        slug: story?.slug,
                        subtypeObject: ((allSubtypes || []).find(item => item._id === selected_subtype))!,
                        primaryVerticalObject: ((allVerticals || []).find(item => item._id === selected_primary_vertical))!,
                        sub_vertical: selected_sub_vertical
                    }, true, false)}
                </span>
            </p>

            <SystemTooltip
                message={selected_verticals.length === 0 ? 'Please select a section first' : ''}
            >
                <ControlledRadio
                    valueKey='_id'
                    labelKey='label'
                    name='primary_vertical'
                    options={verticalsForPrimaryVertical || {}}
                    allowMultiSelect={false}
                    distribution={{
                        type: selected_verticals.length > 1 ? 'grid' : 'row'
                    }}
                    showRadio
                    disabled={selected_verticals.length === 0}
                />
            </SystemTooltip>

            {[SUBTYPES.RESEARCH.ID].includes(selected_subtype) && (
                <>
                    <p className='mt-2 font-medium'>Sub vertical</p>

                    <ControlledRadio
                        valueKey='_id'
                        labelKey='label'
                        name='sub_vertical'
                        options={{
                            SubVerticals: [
                                {
                                    _id: 'internal',
                                    slug: 'internal',
                                    label: 'Internal',
                                    groupKey: 'SubVerticals',
                                },
                                {
                                    _id: 'external',
                                    slug: 'external',
                                    label: 'External',
                                    groupKey: 'SubVerticals',
                                },
                            ]
                        }}
                        allowMultiSelect={false}
                        showRadio
                    />
                </>
            )}
        </LabeledSection>
    )

    const authorsComponent = (
        <LabeledSection
            label='Author'
        >
            <ControlledDropdown
                name='authors'
                label='Add Author'
                valueKey='_id'
                allowMultiSelect
                options={authorsLookupsToOptions}
                inputSize='full'
                shouldShortenLabel
                maxChipsCount={1}
                errorOptions={{
                    show: true,
                    extra_paths: ['at-least-one-author-or-one-guest']
                }}
            />

            <div className='flex items-center justify-between gap-8'>
                <div className='flex items-center flex-wrap xl:gap-2 gap-1'>
                    {selected_guests.map((guest) => {
                        return (
                            <Chip
                                key={guest._id}
                                rightIcon={(
                                    <CancelButton
                                        size={20}
                                        onClick={() => handleRemoveGuest(guest)}
                                    />
                                )}
                            >
                                {guest.first_name} {guest.last_name} (Guest)
                            </Chip>
                        )
                    })}
                </div>

                <div className='flex items-center xl:gap-2 gap-1'>
                    <AddStoryGuestModal />

                    <Button
                        inputVariant='deep_ocean_text_white_bg'
                        disabled={selected_authors.includes(userProfile?._id || '')}
                        onClick={handleAuthorAddMe}
                        rightIcon={<HiOutlineUserPlus size={18} />}
                    >
                        Add me
                    </Button>
                </div>
            </div>
        </LabeledSection>
    )

    const assetsComponent = (
        <SelectAssetModal />
    )

    const tagsComponent = (
        <LabeledSection
            label='Related topics'
            className='grow'
        >
            <ControlledDropdown
                label='Add Related Topics'
                as='with-options-loader'
                optionsQueryKey='tagsLookupsQuery'
                valueKey='_id'
                name='tags'
                inputSize='full'
                allowMultiSelect
                maxChipsCount={1}
                customRenderLabel={(option) => (
                    <span className='flex items-center gap-4'>
                        <span>{option.label}</span>
                        <span>{(option as any).tag_story_aggregate}</span>
                    </span>
                )}
            />

            <div className='flex justify-end'>
                <EmbedTopicModal />
            </div>
        </LabeledSection>
    )

    const metaComponent = (
        <>
            <LabeledSection
                label='Social media'
            >
                <ControlledSystemTextInput
                    name='description'
                    placeholder='Add social media description (up to 200 characters)'
                    inputSize='full'
                    multiline
                    rows={3}
                />
            </LabeledSection>

            <LabeledSection
                label='Meta title'
                className='my-2'
            >
                <ControlledSystemTextInput
                    name='meta_data.title'
                    placeholder='Add meta title'
                    className='w-full'
                />
            </LabeledSection>

            <LabeledSection
                label='Website URL'
                rightIcon={
                    <CopyToClipboardButton
                        text={generateStoryLink(story)}
                        disabled={(story?.story_workflow !== WORKFLOWS.Published || !story?.url)}
                    />
                }
                className='my-2 grow'
            >
                <p className="select-all">
                    {(story?.story_workflow === WORKFLOWS.Published && story?.url) ? generateStoryLink(story) : VALUES.EMPTY_FIELD}
                </p>

                <div className='self-end flex items-center xl:gap-2 gap-1'>
                    <RegenerateURLMenu />
                </div>
            </LabeledSection>

            <LabeledSection>
                <div className='flex items-end justify-between'>
                    <div className='w-fit grid grid-cols-2 grid-rows-2 gap-1'>
                        {[
                            { id: 1, title: 'Created', value: story?.story_created_at },
                            { id: 2, title: 'First Published', value: story?.story_first_publish_date || story?.story_publish_date },
                            { id: 3, title: 'Displayed at', value: story?.story_display_date },
                        ].map((item) => (
                            <React.Fragment key={item.id}>
                                <p>
                                    <span className='font-bold'>{item.title}: </span>
                                </p>
                                <p>
                                    {
                                        item.value ? (
                                            <time dateTime={item.value}>{renderDateTime(item.value)}</time>
                                        ) :
                                            <span>{VALUES.EMPTY_FIELD}</span>
                                    }
                                </p>
                            </React.Fragment>
                        ))}
                    </div>

                    <ViewLogModal />
                </div>
            </LabeledSection>
        </>
    )

    if (presentationVersion === 'compact') {
        return (
            <ControlledSystemMultiTab
                tabs={[
                    {
                        label: 'Publishing',
                        content: (
                            <div
                                className='flex flex-col gap-1'
                            >
                                {verticalsComponent}
                            </div>
                        )
                    },
                    {
                        label: 'Media',
                        content: (
                            <div
                                className='flex flex-col gap-1'
                            >
                                {assetsComponent}
                                {authorsComponent}
                                {tagsComponent}
                            </div>
                        )
                    },
                    {
                        label: 'Metadata',
                        content: (
                            <div
                                className='flex flex-col gap-1'
                            >
                                {metaComponent}
                            </div>
                        )
                    }
                ]}
                labelOptions={{
                    itemClassName: 'w-full'
                }}
            />
        )
    }

    return (
        <div
            className={clsx(
                'bg-cs-200 outline-none text-cs-700 w-full transition-all duration-300',
                'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 px-4',
                'md:divide-x md:divide-cs-300',
                isGeneralSettingsOpen ? 'max-h-[750px] py-4 opacity-100' : 'overflow-hidden max-h-0 py-0 opacity-0',
                mode === 'readonly' && 'opacity-50! pointer-events-none'
            )}
        >
            <LabeledSection
                className='h-full px-2'
            >
                {verticalsComponent}
                {authorsComponent}
            </LabeledSection>

            <LabeledSection
                className='h-full px-2'
            >
                {tagsComponent}
                {assetsComponent}
            </LabeledSection>

            <LabeledSection
                className='h-full px-2'
            >
                {rightColumnOptions.show && (
                    metaComponent
                )}
            </LabeledSection>
        </div>
    )
}
